import { api } from 'helpers'
import axios from 'axios'
import { ARC_MOSAICO_ENDPOINTS } from 'api/arcMosaico'
import type {
  Group,
  InviteCodeData,
  BatchCreateResponse,
} from '../components/AddStudentsModal/interfaces'

interface TutorInviteApiResponse {
  inviteToken: string
  expiresAt: string
  tutor: { id: string; name: string }
}

interface GroupApiResponse {
  id: number
  name: string
}

export async function getGroups(): Promise<Group[]> {
  try {
    const response = await api.get<GroupApiResponse[]>(
      '/api/tutors/my_groups'
    )
    return response.data.map(
      g => ({ id: String(g.id), name: g.name })
    )
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      throw new Error('Unauthorized')
    }
    throw new Error('Failed to load groups')
  }
}

interface SchoolGroupsApiResponse {
  groups?: Array<{ id: string | number; name: string }>
}

/**
 * School-wide classes via GET /api/arc_mosaico/groups (manager is allowed —
 * /api/tutors/my_groups is tutor-only and made the manager's Add Students
 * modal open with an empty class dropdown, AR-735). Backend paginates with a
 * default of 10, so ask for a high limit.
 */
export async function getSchoolGroups(): Promise<Group[]> {
  try {
    const response = await api.get<SchoolGroupsApiResponse>(
      '/api/arc_mosaico/groups',
      { params: { limit: 100 } }
    )
    const groups = response.data?.groups
    return (Array.isArray(groups) ? groups : []).map(
      g => ({ id: String(g.id), name: g.name })
    )
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      throw new Error('Unauthorized')
    }
    throw new Error('Failed to load groups')
  }
}

export async function getGrades(): Promise<
  Array<{ id: string; name: string }>
> {
  try {
    const response = await api.get(
      ARC_MOSAICO_ENDPOINTS.grades.list
    )
    return response.data
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      throw new Error('Unauthorized')
    }
    throw new Error('Failed to load grades')
  }
}

export async function getTutorInviteCode(): Promise<InviteCodeData> {
  try {
    const response = await api.get<TutorInviteApiResponse>(
      ARC_MOSAICO_ENDPOINTS.tutorInvites.show
    )
    return {
      code: response.data.inviteToken,
      url: `${window.location.origin}/account/add-student?code=${response.data.inviteToken}`,
      expiresAt: response.data.expiresAt,
    }
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      throw new Error('Unauthorized')
    }
    throw new Error('Failed to load invite code')
  }
}

export async function createStudentsBatch(
  students: Array<{
    firstName: string
    lastName: string
    username?: string
    gradeId?: string | null
    groupIds?: string[]
  }>
): Promise<BatchCreateResponse> {
  try {
    const payload = {
      students: students.map(s => {
        const entry: Record<string, unknown> = {
          first_name: s.firstName.trim(),
          last_name: s.lastName.trim(),
        }
        if (s.username) entry.username = s.username.trim()
        if (s.gradeId) entry.grade_id = s.gradeId
        if (s.groupIds?.length) entry.group_school_term_ids = s.groupIds
        return entry
      }),
    }

    const response = await api.post(
      ARC_MOSAICO_ENDPOINTS.students.batch,
      payload
    )

    const data = response.data
    return {
      success: data.success,
      defaultPassword:
        data.default_password ?? data.defaultPassword,
      addedStudents: (
        data.added_students ?? data.addedStudents ?? []
      ).map((s: Record<string, string>) => ({
        id: s.id,
        firstName: s.first_name ?? s.firstName,
        lastName: s.last_name ?? s.lastName,
        username: s.username,
      })),
      failedStudents: (
        data.failed_students ?? data.failedStudents ?? []
      ).map((s: Record<string, any>) => ({
        index: s.index ?? null,
        firstName: s.first_name ?? s.firstName,
        lastName: s.last_name ?? s.lastName,
        username: s.username ?? '',
        field: s.field ?? null,
        existingClasses: s.existing_classes ?? s.existingClasses ?? [],
        reason: s.reason,
      })),
    }
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status
      if (status === 401) throw new Error('Unauthorized')
      if (status === 403) throw new Error('Forbidden')
      if (status === 422) {
        const msg =
          error.response?.data?.errors ??
          error.response?.data?.message ??
          'Validation error'
        throw new Error(
          typeof msg === 'string' ? msg : JSON.stringify(msg)
        )
      }
    }
    throw new Error('Failed to create students')
  }
}
